import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { SparkIcon } from '../SparkIcon';
import './ChoiceCard.css';

export type ChoiceCardLayout = 'vertical' | 'horizontal';
export type ChoiceCardSize = 'default' | 'large';

/** Selectable icon + label card (Figma "Tool Discovery" cards: Manual / MCP / Swagger). */
export type ChoiceCardProps = {
  /**
   * Foundation icon slug rendered at 24px (40px for `size="large"`) above
   * (vertical) or before (horizontal) the label. Omit for text-only cards.
   */
  iconSlug?: string;
  /** Card label (base/semibold, centered). */
  label: ReactNode;
  /**
   * Optional supporting copy under the label (detail/regular, centered) —
   * Figma 9952:24753 "Serial / Parallel" and the prediction-strategy cards.
   */
  description?: ReactNode;
  /**
   * `vertical` (default) stacks the icon over the label and stretches to share
   * the row; `horizontal` is the compact 200px pill-card with the icon inline
   * (Figma 4012:16639 "Add Another One").
   */
  layout?: ChoiceCardLayout;
  /**
   * `large` is the 128px format card (Figma 9952:24591 "Model Raw Files /
   * Docker Image"): 40px icon, 20px vertical padding, 8px icon/label gap.
   */
  size?: ChoiceCardSize;
  /** Marks the card as the current choice (active border + tint). */
  selected?: boolean;
  disabled?: boolean;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'disabled' | 'type'>;

/**
 * ChoiceCard — a clickable card with a 24px foundation icon and a centered
 * label, used for mutually exclusive picks laid out in a row (each card
 * stretches to share the row width equally).
 *
 * Chrome follows Figma node 4006:14524: 1px `--semantic-border-default`,
 * 12px radius, 16px padding, 4px gap between icon and label. The
 * `horizontal` layout follows 4012:16639: 200px wide, 12px/16px padding,
 * icon and label on one line.
 */
export const ChoiceCard = forwardRef<HTMLButtonElement, ChoiceCardProps>(function ChoiceCard(
  {
    iconSlug,
    label,
    description,
    layout = 'vertical',
    size = 'default',
    selected = false,
    disabled = false,
    className = '',
    ...rest
  },
  ref,
) {
  const classes = [
    'spark-choice-card',
    layout === 'horizontal' ? 'spark-choice-card-horizontal' : '',
    size === 'large' ? 'spark-choice-card-large' : '',
    description ? 'spark-choice-card-described' : '',
    selected ? 'spark-choice-card-selected' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      aria-pressed={selected}
      disabled={disabled}
      data-layout={layout}
      data-size={size}
      {...rest}
    >
      {iconSlug ? (
        <SparkIcon slug={iconSlug} size={size === 'large' ? 'large' : 'medium'} className="spark-choice-card-icon" />
      ) : null}
      <span className="spark-choice-card-label">{label}</span>
      {description ? <span className="spark-choice-card-description">{description}</span> : null}
    </button>
  );
});
